import { AxiosError } from "axios";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { ApiRoutes } from "../configuration/apiRoutes";
import { defaultLocale } from "../constants/languages";
import { getSystemConfigs } from "../features/systemConfigs";
import { useAppDispatch } from "../hooks/redux";
import { LocaleDto } from "../models/LocaleDto";
import { LocalesResponse } from "../models/LocalesResponse";
import { SettingGroupType } from "../models/SettingGroupType";
import { RestService } from "../utils/axios";
import dayjs from "dayjs";

interface LocaleContextState {
    locales: Array<LocaleDto>;
}

export const LocaleContext = React.createContext<
    LocaleContextState | undefined
>(undefined);

interface ILocaleProviderProps {
    children: JSX.Element | JSX.Element[];
}

export const LocaleProvider: React.FC<ILocaleProviderProps> = (
    props: ILocaleProviderProps
) => {
    const { locale } = useParams();
    const { i18n } = useTranslation();
    const dispatch = useAppDispatch();

    const [state, setState] = useState<LocaleContextState | undefined>(
        undefined
    );

    const loadDayjsLocale = async (locale: string) => {
        try {
            await import(`dayjs/locale/${locale.split("-")[0]}.js`);
            dayjs.locale(locale);
        } catch (error) {
            console.error(`Failed to load the locale: ${locale}`, error);
        }
    };

    useEffect(() => {
        const getLanguages = () => {
            RestService.Get<LocalesResponse>(
                ApiRoutes.Localization.Locales(),
                (response: LocalesResponse) => {
                    const newState: LocaleContextState = {
                        locales: response.items,
                    };
                    setState(newState);
                },
                (error: AxiosError) => {
                    console.log(`Error: ${error.message}`);
                }
            );
        };

        getLanguages();
    }, []);

    const refreshLocalization = (localeCode: string): void => {
        RestService.Get(
            ApiRoutes.Localization.I18n(localeCode),
            (response: any) => {
                if (!response || response[localeCode] === undefined) return;

                Object.keys(response[localeCode]).forEach((key) => {
                    i18n.addResourceBundle(
                        localeCode,
                        key,
                        response[localeCode][key],
                        true,
                        true
                    );
                });
                i18n.changeLanguage(localeCode);
                loadDayjsLocale(localeCode);
            },
            (error: AxiosError) => {
                console.log("Error: " + error.message);
            }
        );
    };

    useEffect(() => {
        refreshLocalization(locale ?? defaultLocale);

        dispatch(
            getSystemConfigs({
                localeCode: locale ?? defaultLocale,
                type: SettingGroupType.frontOfficeNotifications,
            })
        );

        dispatch(
            getSystemConfigs({
                localeCode: locale ?? defaultLocale,
                type: SettingGroupType.frontOfficeSystemNotifications,
            })
        );
    }, [locale]);

    return (
        <LocaleContext.Provider value={state}>
            {props.children}
        </LocaleContext.Provider>
    );
};
